import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Mail, RefreshCw } from "lucide-react";
import BrandLogo from "@/components/marketing/BrandLogo";

export default function VerifyEmail() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  if (user?.email_verified) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.post("/auth/verify-otp", { code: code.trim() });
      toast.success("Verified! Welcome aboard.");
      await refresh?.();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not verify");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-otp");
      toast.success("New code sent to your email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6" data-testid="verify-email-page">
      <div className="w-full max-w-md bg-white rounded-[6px] border border-[#E2E8F0] p-8 shadow-sm">
        <div className="mb-5"><BrandLogo size="md" /></div>
        <div className="w-12 h-12 rounded-[6px] bg-[#F97316]/12 text-[#F97316] flex items-center justify-center mb-5">
          <Mail className="w-6 h-6" />
        </div>
        <h1 className="font-display font-extrabold text-2xl">Verify your email</h1>
        <p className="text-sm text-[#4B5563] mt-2">
          We sent a 6-digit code to <span className="font-mono">{user?.email}</span>. Enter it below to activate your account.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="123456"
            autoFocus
            className="hs-input font-mono text-center text-2xl tracking-[0.5em] py-3"
            data-testid="otp-input"
          />
          <button
            type="submit"
            disabled={busy || code.length !== 6}
            className="btn-primary w-full disabled:opacity-60"
            data-testid="otp-submit"
          >
            {busy ? "Verifying…" : "Verify email"}
          </button>
        </form>
        <button
          onClick={resend}
          disabled={resending}
          className="text-sm text-[#0044FF] hover:underline mt-5 inline-flex items-center gap-2"
          data-testid="otp-resend"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} />
          {resending ? "Sending…" : "Resend code"}
        </button>
        <p className="text-xs text-[#4B5563] mt-6 leading-relaxed">
          Code expires in 15 minutes. Wrong email? <a href="/login" className="text-[#0044FF] hover:underline">Sign in with another account</a>.
        </p>
      </div>
    </div>
  );
}
